import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { EyeIcon, EyeOffIcon } from '@/components/icons';
import './Input.css';

/**
 * Input component props
 */
export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Label text for the input
   */
  label?: string;

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Helper text to display below the input
   */
  helperText?: string;

  /**
   * Whether the input is required (marks the label and sets the native attribute;
   * forms with custom validation should use `noValidate`)
   */
  required?: boolean;

  /**
   * Icon to display at the start of the input
   */
  leftIcon?: ReactNode;

  /**
   * Icon to display at the end of the input
   */
  rightIcon?: ReactNode;

  /**
   * Input size variant
   * @default 'md'
   */
  inputSize?: 'sm' | 'md' | 'lg';

  /**
   * Whether to show password toggle for password inputs
   * @default true
   */
  showPasswordToggle?: boolean;
}

/**
 * Input component with label, error handling, and icons
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email"
 *   error={errors.email}
 * />
 *
 * <Input
 *   label="Password"
 *   type="password"
 *   error={errors.password}
 *   showPasswordToggle
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      required,
      leftIcon,
      rightIcon,
      inputSize = 'md',
      showPasswordToggle = true,
      type = 'text',
      className = '',
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    const [showPassword, setShowPassword] = useState(false);

    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;

    const inputClassNames = [
      'input-field',
      `input-${inputSize}`,
      error ? 'input-error' : '',
      leftIcon ? 'input-with-left-icon' : '',
      rightIcon || (isPassword && showPasswordToggle)
        ? 'input-with-right-icon'
        : '',
      disabled ? 'input-disabled' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    return (
      <div className="input-wrapper">
        {label && (
          <label htmlFor={inputId} className="input-label">
            {label}
            {required && <span className="input-required">*</span>}
          </label>
        )}

        <div className="input-container">
          {leftIcon && <span className="input-icon input-icon-left">{leftIcon}</span>}

          <input
            ref={ref}
            id={inputId}
            type={inputType}
            className={inputClassNames}
            disabled={disabled}
            required={required}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error ? errorId : helperText ? helperId : undefined
            }
            {...props}
          />

          {isPassword && showPasswordToggle && (
            <button
              type="button"
              className="input-icon input-icon-right input-password-toggle"
              onClick={togglePasswordVisibility}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon size={20} />}
            </button>
          )}

          {!isPassword && rightIcon && (
            <span className="input-icon input-icon-right">{rightIcon}</span>
          )}
        </div>

        {error && (
          <span id={errorId} className="input-error-message" role="alert">
            {error}
          </span>
        )}

        {!error && helperText && (
          <span id={helperId} className="input-helper-text">
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
